import { useState } from "react";
import {
  ArrowLeft,
  Smartphone,
  Watch,
  Activity,
  Wifi,
  WifiOff,
  Plus,
  Settings,
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import welcomeBg from "../../assets/homebg.png";

const ConnectedDevicesPage = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([
    {
      id: 1,
      name: "iPhone 14 Pro",
      type: "smartphone",
      icon: Smartphone,
      connected: true,
      lastSync: "2 minutes ago",
      battery: 85,
    },
    {
      id: 2,
      name: "Apple Watch Series 9",
      type: "watch",
      icon: Watch,
      connected: true,
      lastSync: "5 minutes ago",
      battery: 92,
    },
    {
      id: 3,
      name: "Fitbit Charge 5",
      type: "fitness",
      icon: Activity,
      connected: false,
      lastSync: "2 hours ago",
      battery: 45,
    },
  ]);

  const toggleConnection = (deviceId: number) => {
    setDevices(
      devices.map(device =>
        device.id === deviceId
          ? { ...device, connected: !device.connected }
          : device
      )
    );
  };

  const getStatusColor = (connected: boolean) => {
    return connected ? "text-green-600" : "text-muted-foreground";
  };

  const getStatusBg = (connected: boolean) => {
    return connected ? "bg-green-50 dark:bg-green-950" : "bg-muted";
  };

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/glucera/profilepage")}
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-title2 font-bold text-foreground">
            Connected Devices
          </h1>
          <div className="w-10" />
        </div>

        {/* Connection Status */}
        <Card className="rounded-2xl border border-border bg-card p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-950">
                <Wifi className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Device Sync</h3>
                <p className="text-subhead text-muted-foreground">
                  2 devices connected
                </p>
              </div>
            </div>
            <div className="h-3 w-3 animate-pulse rounded-full bg-green-500" />
          </div>
        </Card>

        {/* Connected Devices List */}
        <div className="space-y-3">
          {devices.map(device => {
            const Icon = device.icon;
            return (
              <Card
                key={device.id}
                className="rounded-2xl border border-border bg-card p-4 shadow-lg"
              >
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <div
                      className={`h-12 w-12 flex-shrink-0 rounded-full ${getStatusBg(device.connected)} flex items-center justify-center`}
                    >
                      <Icon
                        className={`h-6 w-6 ${getStatusColor(device.connected)}`}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate font-semibold text-foreground">
                        {device.name}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="flex items-center gap-1">
                          {device.connected ? (
                            <Wifi className="h-4 w-4 text-green-500" />
                          ) : (
                            <WifiOff className="h-4 w-4 text-muted-foreground" />
                          )}
                          <span
                            className={`text-caption1 ${device.connected ? "text-green-600" : "text-muted-foreground"}`}
                          >
                            {device.connected ? "Connected" : "Disconnected"}
                          </span>
                        </div>
                        <span className="text-muted-foreground">•</span>
                        <span className="text-caption1 text-muted-foreground">
                          {device.lastSync}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    {device.connected && (
                      <div className="flex items-center gap-1 text-subhead">
                        <span className="text-muted-foreground">Battery:</span>
                        <span className="font-medium text-foreground">
                          {device.battery}%
                        </span>
                      </div>
                    )}
                    <Button
                      variant={device.connected ? "outline" : "default"}
                      size="sm"
                      onClick={() => toggleConnection(device.id)}
                      className={`ml-auto rounded-lg ${
                        device.connected
                          ? "border-red-200 text-red-600 hover:bg-red-50"
                          : "bg-gradient-primary text-primary-foreground shadow-primary hover:opacity-90"
                      }`}
                    >
                      {device.connected ? "Disconnect" : "Connect"}
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Add New Device */}
        <Card className="rounded-2xl border border-dashed border-border bg-card p-4 shadow-lg">
          <div className="flex cursor-pointer items-center justify-center gap-3 text-muted-foreground transition-colors hover:text-foreground">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <Plus className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-medium">Add New Device</h3>
              <p className="text-subhead">
                Connect your fitness tracker or smartwatch
              </p>
            </div>
          </div>
        </Card>

        {/* Sync Settings */}
        <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
          <h3 className="mb-4 font-semibold text-foreground">Sync Settings</h3>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-foreground">Auto Sync</p>
                <p className="text-subhead text-muted-foreground">
                  Automatically sync data every 15 minutes
                </p>
              </div>
              <div className="relative h-6 w-12 cursor-pointer rounded-full bg-blue-500">
                <div className="absolute right-1 top-1 h-4 w-4 rounded-full bg-background transition-transform" />
              </div>
            </div>

            <div className="h-px bg-border" />

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-foreground">Background Sync</p>
                <p className="text-subhead text-muted-foreground">
                  Sync data when app is closed
                </p>
              </div>
              <div className="relative h-6 w-12 cursor-pointer rounded-full bg-muted">
                <div className="absolute left-1 top-1 h-4 w-4 rounded-full bg-background transition-transform" />
              </div>
            </div>

            <div className="h-px bg-border" />

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-foreground">Data Sharing</p>
                <p className="text-subhead text-muted-foreground">
                  Share health data with connected apps
                </p>
              </div>
              <Button variant="ghost" size="icon" className="text-gray-400">
                <Settings className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>

        {/* Sync History */}
        <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
          <h3 className="mb-4 font-semibold text-foreground">
            Recent Sync Activity
          </h3>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="h-2 w-2 rounded-full bg-green-500" />
              <div className="flex-1">
                <p className="text-subhead text-foreground">
                  Health data synced successfully
                </p>
                <p className="text-caption1 text-muted-foreground">2 minutes ago</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-2 w-2 rounded-full bg-green-500" />
              <div className="flex-1">
                <p className="text-subhead text-foreground">
                  Fitness tracker connected
                </p>
                <p className="text-caption1 text-muted-foreground">1 hour ago</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-2 w-2 rounded-full bg-yellow-500" />
              <div className="flex-1">
                <p className="text-subhead text-foreground">
                  Sync interrupted - retrying
                </p>
                <p className="text-caption1 text-muted-foreground">3 hours ago</p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ConnectedDevicesPage;
